import React, { useState } from 'react';
import {
  LifeBuoy,
  Search,
  BookOpen,
  MessageSquare,
  ShieldCheck,
  Calculator,
  CreditCard,
  Code2,
  FileQuestion,
  CheckCircle2,
  ExternalLink,
  ChevronRight,
  Sparkles,
  Lock,
  ArrowRight
} from 'lucide-react';
import { AppView } from '../../types';
import { supportEngine } from '../../services/support/supportEngine';
import { KnowledgeArticle, KnownIssue, SupportDomain } from '../../services/support/supportTypes';

interface GlobalHelpCenterPortalProps {
  onNavigateToView?: (view: AppView) => void;
}

export const GlobalHelpCenterPortal: React.FC<GlobalHelpCenterPortalProps> = ({
  onNavigateToView
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDomain, setSelectedDomain] = useState<string>('ALL');
  const [selectedArticle, setSelectedArticle] = useState<KnowledgeArticle | null>(null);
  const [feedbackGiven, setFeedbackGiven] = useState<Record<string, 'HELPFUL' | 'NOT_HELPFUL'>>({});

  // Contact modal / form state
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [ticketSubject, setTicketSubject] = useState('');
  const [ticketDomain, setTicketDomain] = useState<SupportDomain>('CALCULATORS');
  const [ticketEmail, setTicketEmail] = useState('');
  const [ticketName, setTicketName] = useState('');
  const [ticketMessage, setTicketMessage] = useState('');
  const [developerReqId, setDeveloperReqId] = useState('');
  const [submittedCaseId, setSubmittedCaseId] = useState<string | null>(null);

  const articles = supportEngine.getKnowledgeArticles();
  const knownIssues = supportEngine.getKnownIssues();

  const filteredArticles = articles.filter(art => {
    const matchesSearch =
      searchQuery === '' ||
      art.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      art.summary.toLowerCase().includes(searchQuery.toLowerCase()) ||
      art.tags.some(t => t.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesDomain =
      selectedDomain === 'ALL' ||
      (selectedDomain === 'CALCULATORS' && (art.domain === 'CALCULATORS' || art.domain === 'TAX' || art.domain === 'MORTGAGE')) ||
      (selectedDomain === 'DEVELOPER' && art.domain === 'DEVELOPER_API') ||
      (selectedDomain === 'BILLING' && (art.domain === 'BILLING' || art.domain === 'PAYMENT')) ||
      (selectedDomain === 'PRIVACY' && (art.domain === 'PRIVACY' || art.domain === 'COMPLIANCE'));

    return matchesSearch && matchesDomain && art.audience !== 'AGENT_INTERNAL_ONLY';
  });

  const handleFeedback = (articleId: string, type: 'HELPFUL' | 'NOT_HELPFUL') => {
    setFeedbackGiven(prev => ({ ...prev, [articleId]: type }));
  };

  const handleCreateTicket = (e: React.FormEvent) => {
    e.preventDefault();
    if (!ticketSubject || !ticketMessage || !ticketEmail) return;

    const created = supportEngine.createCase({
      customerName: ticketName || 'YamooPay User',
      customerEmail: ticketEmail,
      market: 'US',
      locale: 'en-US',
      language: 'en',
      domain: ticketDomain,
      category: `${ticketDomain} User Inquiry`,
      subject: ticketSubject,
      message: ticketMessage,
      developerRequestId: ticketDomain === 'DEVELOPER_API' ? developerReqId : undefined
    });

    setSubmittedCaseId(created.publicId);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A] pb-24">
      {/* 1. Header & Hero */}
      <div className="bg-white border-b border-[#E2E8F0] py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-semibold uppercase tracking-wider mb-4">
            <LifeBuoy className="w-3.5 h-3.5" />
            YamooPay Global Customer Trust & Knowledge Base
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-[#0F172A] tracking-tight mb-4">
            How can we help you succeed with YamooPay?
          </h1>
          <p className="text-base sm:text-lg text-[#64748B] max-w-2xl mx-auto mb-8">
            Access verified financial methodology guides, API references, billing assistance, and statutory calculation rulesets.
          </p>

          {/* Search Box */}
          <div className="relative max-w-2xl mx-auto">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-[#94A3B8]">
              <Search className="w-5 h-5" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search tax rules, APR formulas, rate limits, VAT reverse charge, DSAR..."
              className="w-full pl-11 pr-4 py-3.5 bg-white border-2 border-[#CBD5E1] hover:border-[#94A3B8] focus:border-[#2563EB] focus:ring-4 focus:ring-blue-100 rounded-xl text-sm font-medium transition-all shadow-xs outline-none"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute inset-y-0 right-0 pr-4 flex items-center text-xs font-bold text-[#64748B] hover:text-[#0F172A]"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        {/* 2. Known Issues & Service Status Banner */}
        {knownIssues.length > 0 && (
          <div className="mb-8 space-y-3">
            {knownIssues.map(issue => (
              <div
                key={issue.id}
                className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
              >
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-amber-100 text-amber-800 rounded-lg shrink-0 mt-0.5">
                    <ShieldCheck className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold bg-amber-200 text-amber-900 px-2 py-0.5 rounded">
                        {issue.publicCode}
                      </span>
                      <span className="text-xs font-semibold text-amber-700 uppercase tracking-wide">
                        Status: {issue.status}
                      </span>
                    </div>
                    <h2 className="text-sm font-bold text-amber-950 mt-1">{issue.title}</h2>
                    <p className="text-xs text-amber-800 mt-0.5 leading-relaxed">
                      {issue.symptomsPublic}
                    </p>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <span className="text-[11px] font-medium text-amber-700 block">
                    {issue.safeWorkaround}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* 3. Category Filter Chips */}
        <div className="flex items-center gap-2 overflow-x-auto pb-4 mb-6 text-xs font-bold">
          {[
            { id: 'ALL', label: 'All Knowledge' },
            { id: 'CALCULATORS', label: 'Salary, Tax & Mortgage Math' },
            { id: 'BILLING', label: 'Billing, Invoicing & VAT' },
            { id: 'DEVELOPER', label: 'Developer APIs & SDKs' },
            { id: 'PRIVACY', label: 'Privacy Rights (GDPR/CCPA)' }
          ].map(cat => (
            <button
              key={cat.id}
              onClick={() => {
                setSelectedDomain(cat.id);
                setSelectedArticle(null);
              }}
              className={`px-4 py-2 rounded-lg border transition-all whitespace-nowrap cursor-pointer ${
                selectedDomain === cat.id
                  ? 'bg-blue-600 border-blue-600 text-white shadow-xs'
                  : 'bg-white border-[#E2E8F0] text-[#475569] hover:bg-slate-50'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* 4. Main Content Area: Article List or Active Article Reader */}
        {selectedArticle ? (
          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 sm:p-8 shadow-xs">
            <button
              onClick={() => setSelectedArticle(null)}
              className="text-xs font-bold text-blue-600 hover:text-blue-800 flex items-center gap-1 mb-6 cursor-pointer"
            >
              ← Back to Help Center Articles
            </button>

            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="text-xs font-bold px-2.5 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-full">
                {selectedArticle.domain}
              </span>
              <span className="text-xs font-medium text-[#64748B]">
                Market: {selectedArticle.market} ({selectedArticle.locale})
              </span>
              {selectedArticle.rulesetReference && (
                <span className="text-xs font-mono bg-slate-100 text-slate-700 px-2 py-0.5 rounded">
                  Ruleset: {selectedArticle.rulesetReference}
                </span>
              )}
            </div>

            <h2 className="text-2xl font-black text-[#0F172A] tracking-tight mb-4">
              {selectedArticle.title}
            </h2>

            <div className="text-xs text-[#64748B] flex items-center gap-4 pb-6 border-b border-[#E2E8F0] mb-6">
              <span>Reviewed by: <strong>{selectedArticle.reviewedBy}</strong></span>
              <span>Last audit: {new Date(selectedArticle.lastReviewedAt).toLocaleDateString()}</span>
              <span>Product version: {selectedArticle.productVersion}</span>
            </div>

            {/* Markdown Body preview */}
            <div className="prose prose-slate max-w-none text-sm text-[#334155] leading-relaxed space-y-4 mb-8">
              {selectedArticle.contentMarkdown.split('\n\n').map((para, idx) => (
                <p key={idx} className="whitespace-pre-line">{para}</p>
              ))}
            </div>

            {/* Feedback & Satisfaction */}
            <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-xs font-bold text-[#0F172A]">
                Was this article helpful for your calculation or inquiry?
              </div>
              {feedbackGiven[selectedArticle.id] ? (
                <div className="flex items-center gap-2 text-xs font-bold text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-lg">
                  <CheckCircle2 className="w-4 h-4" /> Thank you for your feedback!
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleFeedback(selectedArticle.id, 'HELPFUL')}
                    className="px-3 py-1.5 bg-white border border-[#CBD5E1] hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-300 text-xs font-bold rounded-lg transition-colors cursor-pointer"
                  >
                    👍 Yes ({selectedArticle.helpfulCount})
                  </button>
                  <button
                    onClick={() => handleFeedback(selectedArticle.id, 'NOT_HELPFUL')}
                    className="px-3 py-1.5 bg-white border border-[#CBD5E1] hover:bg-rose-50 hover:text-rose-700 hover:border-rose-300 text-xs font-bold rounded-lg transition-colors cursor-pointer"
                  >
                    👎 No, I need more help
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredArticles.map(article => (
              <div
                key={article.id}
                onClick={() => setSelectedArticle(article)}
                className="bg-white border border-[#E2E8F0] hover:border-blue-300 hover:shadow-md rounded-2xl p-6 transition-all cursor-pointer flex flex-col justify-between group"
              >
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[11px] font-bold px-2 py-0.5 bg-slate-100 text-slate-700 rounded-md">
                      {article.domain}
                    </span>
                    <span className="text-[11px] font-medium text-[#94A3B8]">
                      {article.viewsCount.toLocaleString()} views
                    </span>
                  </div>
                  <h3 className="text-base font-bold text-[#0F172A] group-hover:text-blue-600 transition-colors mb-2">
                    {article.title}
                  </h3>
                  <p className="text-xs text-[#64748B] line-clamp-2 leading-relaxed">
                    {article.summary}
                  </p>
                </div>
                <div className="flex items-center justify-between pt-4 mt-4 border-t border-[#F1F5F9] text-xs font-bold text-blue-600">
                  <span>Read Guide</span>
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* 5. Contact Support & Escalation Callout */}
        <div className="mt-12 bg-linear-to-r from-blue-900 to-indigo-900 text-white rounded-2xl p-8 shadow-md">
          <div className="max-w-3xl">
            <h2 className="text-xl font-black mb-2">Still have questions or found a calculation discrepancy?</h2>
            <p className="text-xs sm:text-sm text-blue-100 mb-6 leading-relaxed">
              Our specialized support desks investigate salary/tax rulesets, mortgage APR math, API Request IDs, and B2B billing issues with full statutory audit trails.
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => {
                  setSubmittedCaseId(null);
                  setIsContactModalOpen(true);
                }}
                className="px-5 py-2.5 bg-white text-blue-900 hover:bg-blue-50 font-bold rounded-xl text-xs transition-colors cursor-pointer shadow-xs"
              >
                Open a Support Ticket
              </button>
              {onNavigateToView && (
                <button
                  onClick={() => onNavigateToView('customer-billing-portal')}
                  className="px-5 py-2.5 bg-blue-800/80 hover:bg-blue-800 text-white font-bold rounded-xl text-xs transition-colors cursor-pointer border border-blue-700"
                >
                  Manage Invoices & Subscriptions
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Contact Support Modal */}
      {isContactModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white border border-[#E2E8F0] rounded-2xl max-w-xl w-full p-6 sm:p-8 shadow-2xl overflow-y-auto max-h-[90vh]">
            {submittedCaseId ? (
              <div className="text-center py-6">
                <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-extrabold text-[#0F172A] mb-2">Support Case Created!</h3>
                <p className="text-xs text-[#64748B] mb-4">
                  Your case reference is <strong className="text-blue-600 font-mono text-sm">{submittedCaseId}</strong>.
                  A confirmation has been sent to your email with SLA tracking.
                </p>
                <button
                  onClick={() => {
                    setIsContactModalOpen(false);
                    setSubmittedCaseId(null);
                  }}
                  className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold transition-colors cursor-pointer"
                >
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={handleCreateTicket} className="space-y-4">
                <div className="flex items-center justify-between pb-3 border-b border-[#E2E8F0]">
                  <h3 className="text-lg font-bold text-[#0F172A]">Open Support Case</h3>
                  <button
                    type="button"
                    onClick={() => setIsContactModalOpen(false)}
                    className="text-xs font-bold text-[#64748B] hover:text-[#0F172A]"
                  >
                    ✕ Close
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-[#334155] mb-1">Your Full Name</label>
                    <input
                      type="text"
                      required
                      value={ticketName}
                      onChange={e => setTicketName(e.target.value)}
                      placeholder="e.g. Sarah Jenkins"
                      className="w-full px-3 py-2 border border-[#CBD5E1] rounded-lg text-xs outline-none focus:border-blue-600"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-[#334155] mb-1">Email Address</label>
                    <input
                      type="email"
                      required
                      value={ticketEmail}
                      onChange={e => setTicketEmail(e.target.value)}
                      placeholder="name@company.com"
                      className="w-full px-3 py-2 border border-[#CBD5E1] rounded-lg text-xs outline-none focus:border-blue-600"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#334155] mb-1">Inquiry Domain / Category</label>
                  <select
                    value={ticketDomain}
                    onChange={e => setTicketDomain(e.target.value as SupportDomain)}
                    className="w-full px-3 py-2 border border-[#CBD5E1] rounded-lg text-xs outline-none focus:border-blue-600 bg-white"
                  >
                    <option value="CALCULATORS">Salary, Tax & Calculation Math</option>
                    <option value="BILLING">Billing, Invoices & Subscriptions</option>
                    <option value="DEVELOPER_API">Developer API / Request ID Debugging</option>
                    <option value="AUTHENTICATION">Account & Security</option>
                    <option value="PRIVACY">GDPR/CCPA Privacy Request</option>
                    <option value="ACCESSIBILITY">Accessibility (A11y) Feedback</option>
                    <option value="GENERAL">General Inquiries</option>
                  </select>
                </div>

                {ticketDomain === 'DEVELOPER_API' && (
                  <div>
                    <label className="block text-xs font-bold text-[#334155] mb-1">API Request ID (Optional)</label>
                    <input
                      type="text"
                      value={developerReqId}
                      onChange={e => setDeveloperReqId(e.target.value)}
                      placeholder="e.g. req_yp_live_4490182"
                      className="w-full px-3 py-2 border border-[#CBD5E1] rounded-lg text-xs font-mono outline-none focus:border-blue-600"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-xs font-bold text-[#334155] mb-1">Subject</label>
                  <input
                    type="text"
                    required
                    value={ticketSubject}
                    onChange={e => setTicketSubject(e.target.value)}
                    placeholder="Brief description of your question or calculation scenario"
                    className="w-full px-3 py-2 border border-[#CBD5E1] rounded-lg text-xs outline-none focus:border-blue-600"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#334155] mb-1">Detailed Explanation & Figures</label>
                  <textarea
                    required
                    rows={4}
                    value={ticketMessage}
                    onChange={e => setTicketMessage(e.target.value)}
                    placeholder="Please include exact figures, tax code, jurisdiction, or browser context..."
                    className="w-full px-3 py-2 border border-[#CBD5E1] rounded-lg text-xs outline-none focus:border-blue-600"
                  />
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-[11px] text-[#64748B] flex items-start gap-2">
                  <Lock className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" />
                  <span>
                    Privacy Notice: Never include full credit card numbers, passwords or CVC secrets. Support tickets are protected under YamooPay Enterprise Security Policy.
                  </span>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setIsContactModalOpen(false)}
                    className="px-4 py-2 border border-[#CBD5E1] text-[#475569] rounded-lg text-xs font-bold hover:bg-slate-50 cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold transition-colors cursor-pointer"
                  >
                    Submit Support Case
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
